import { useMemo } from "react";
import { diff_match_patch, type Diff } from "diff-match-patch";
import { Sparkles, X } from "lucide-react";

// Mejora "Revisar cambios de IA" (changelog cliente): cuando el usuario
// pulsa "Aplicar sugerencias", en vez de machacar el contenido actual de
// la sección, mostramos este modal comparativo lado a lado:
//   - Izquierda ("Actual"): el texto que tiene ahora, con lo que se
//     ELIMINA tachado en rojo.
//   - Derecha ("Propuesta IA"): el texto refinado, con lo AÑADIDO
//     resaltado en verde.
// Solo al "Aceptar" se aplica el HTML refinado real. Así el usuario
// mantiene la trazabilidad y decide.
//
// Nota técnica: el diff se calcula sobre TEXTO PLANO extraído de ambos
// HTML (legible y robusto). El HTML refinado completo se aplica íntegro
// al aceptar — no intentamos reconstruir HTML a partir del diff, que
// sería frágil con tablas/listas.

interface DiffPreviewModalProps {
  currentHtml: string;
  refinedHtml: string;
  onAccept: () => void;
  onReject: () => void;
}

/** Extrae texto plano legible de un fragmento HTML (vía DOM del navegador). */
function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  // textContent colapsa los tags; normalizamos espacios y saltos.
  const text = doc.body.textContent ?? "";
  return text.replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").trim();
}

type Segment = { text: string; kind: "equal" | "removed" | "added" };

/**
 * Calcula el diff a nivel de palabra entre dos textos. Devuelve dos
 * listas de segmentos: la del lado "actual" (equal + removed) y la del
 * lado "propuesta" (equal + added).
 */
function computeDiff(current: string, refined: string): {
  left: Segment[];
  right: Segment[];
  hasChanges: boolean;
} {
  const dmp = new diff_match_patch();
  // Diff a nivel de "palabra" para que sea legible: tokenizamos por
  // límites de palabra usando el truco line-mode de dmp sobre palabras.
  const diffs: Diff[] = dmp.diff_main(current, refined);
  dmp.diff_cleanupSemantic(diffs);

  const left: Segment[] = [];
  const right: Segment[] = [];
  let hasChanges = false;

  for (const [op, text] of diffs) {
    if (op === 0) {
      left.push({ text, kind: "equal" });
      right.push({ text, kind: "equal" });
    } else if (op === -1) {
      left.push({ text, kind: "removed" });
      hasChanges = true;
    } else if (op === 1) {
      right.push({ text, kind: "added" });
      hasChanges = true;
    }
  }
  return { left, right, hasChanges };
}

function SegmentView({ segments, side }: { segments: Segment[]; side: "left" | "right" }) {
  return (
    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
      {segments.map((seg, i) => {
        if (seg.kind === "equal") return <span key={i}>{seg.text}</span>;
        if (seg.kind === "removed" && side === "left") {
          return (
            <span
              key={i}
              className="bg-red-100 text-red-800 line-through rounded px-0.5"
            >
              {seg.text}
            </span>
          );
        }
        if (seg.kind === "added" && side === "right") {
          return (
            <span key={i} className="bg-green-100 text-green-800 rounded px-0.5">
              {seg.text}
            </span>
          );
        }
        return null;
      })}
    </div>
  );
}

export function DiffPreviewModal({
  currentHtml,
  refinedHtml,
  onAccept,
  onReject,
}: DiffPreviewModalProps) {
  const { left, right, hasChanges } = useMemo(() => {
    const current = htmlToPlainText(currentHtml);
    const refined = htmlToPlainText(refinedHtml);
    return computeDiff(current, refined);
  }, [currentHtml, refinedHtml]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Revisar cambios sugeridos por la IA"
      onClick={onReject}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Revisar cambios sugeridos
            </h2>
          </div>
          <button
            type="button"
            onClick={onReject}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 text-sm text-gray-500">
          Revisa lo que la IA propone cambiar. En{" "}
          <span className="bg-red-100 text-red-800 line-through px-1 rounded">
            rojo
          </span>{" "}
          lo que se elimina, en{" "}
          <span className="bg-green-100 text-green-800 px-1 rounded">verde</span>{" "}
          lo que se añade. Nada se aplica hasta que pulses{" "}
          <em>Aceptar cambios</em>.
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-200">
          <div className="p-5 min-w-0">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Actual
            </h3>
            {hasChanges ? (
              <SegmentView segments={left} side="left" />
            ) : (
              <p className="text-sm text-gray-400 italic">Sin cambios.</p>
            )}
          </div>
          <div className="p-5 min-w-0 bg-gray-50/40">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-purple-600 mb-3 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              Propuesta IA
            </h3>
            <SegmentView segments={right} side="right" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onReject}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            Aceptar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

export default DiffPreviewModal;
